import { supabase, isSupabaseConfigured, getCurrentUserId } from "@/lib/supabase";
import type { UserProfile, InsertUserProfile } from "@shared/schema";
import {
  DEFAULT_DOCUMENT_NAMING_PREFERENCES,
  resolveDocumentNamingPreferences,
  type DocumentNamingPreferences,
} from "@/lib/documentNamingPreferences";
import {
  normalizeLibraryDocRefs,
  type LibraryDocRef,
} from "@/lib/libraryQuickAccess";
import {
  MAX_SOURCE_OF_TRUTH_DOCS,
  normalizeSourceOfTruthDocIds,
  isSourceOfTruthEnabledFromPrefs,
} from "@/lib/sourceOfTruthPreferences";

// Type definition for AI Response Style Preferences
export interface ResponsePreferences {
  includeUrls: boolean;           // Always include clickable URLs for mentioned resources
  includeCosts: boolean;          // Show estimated costs when relevant
  includeActionPlans: boolean;    // Provide step-by-step action plans
  useBulletPoints: boolean;       // Prefer bullet points over paragraphs
  includeTimelines: boolean;      // Include time estimates for tasks
  includeAlternatives: boolean;   // Suggest alternative approaches
  citeSources: boolean;           // Cite sources and references
  showConfidence: boolean;        // Show confidence level in responses
  maxParagraphLength: number;     // Max sentences per paragraph (1-5)
  includeExamples: boolean;       // Include practical examples
  includeWarnings: boolean;       // Highlight risks and warnings
  customInstructions: string;     // Free-form additional instructions
}

export const DEFAULT_RESPONSE_PREFERENCES: ResponsePreferences = {
  includeUrls: true,
  includeCosts: true,
  includeActionPlans: true,
  useBulletPoints: true,
  includeTimelines: false,
  includeAlternatives: true,
  citeSources: true,
  showConfidence: false,
  maxParagraphLength: 3,
  includeExamples: true,
  includeWarnings: true,
  customInstructions: "",
};

class UserProfileService {
  private cachedProfile: UserProfile | null | undefined = undefined;
  private inflightProfilePromise: Promise<UserProfile | null> | null = null;

  clearCache() {
    this.cachedProfile = undefined;
    this.inflightProfilePromise = null;
  }

  private setCachedProfile(profile: UserProfile | null) {
    this.cachedProfile = profile;
  }

  async getUserProfile(): Promise<UserProfile | null> {
    if (this.cachedProfile !== undefined) {
      return this.cachedProfile;
    }

    if (this.inflightProfilePromise) {
      return this.inflightProfilePromise;
    }

    this.inflightProfilePromise = this.fetchUserProfile()
      .finally(() => {
        this.inflightProfilePromise = null;
      });

    return this.inflightProfilePromise;
  }

  private async fetchUserProfile(): Promise<UserProfile | null> {
    if (!isSupabaseConfigured) return null;
    const userId = await getCurrentUserId();
    if (!userId) return null;

    const { data, error } = await supabase
      .from("user_profiles")
      .select("*")
      .eq("user_id", userId)
      .single();

    if (error && error.code !== "PGRST116") {
      console.error("Error fetching user profile:", error);
      throw error;
    }

    this.setCachedProfile(data);
    return data;
  }

  async createUserProfile(profile: Partial<InsertUserProfile>): Promise<UserProfile> {
    if (!isSupabaseConfigured) throw new Error("Service not configured");
    const userId = await getCurrentUserId();
    if (!userId) throw new Error("Not authenticated");

    const { data, error } = await supabase
      .from("user_profiles")
      .insert({
        user_id: userId,
        ...profile,
      })
      .select()
      .single();

    if (error) {
      console.error("Error creating user profile:", error);
      throw error;
    }

    this.setCachedProfile(data);
    return data;
  }

  async updateUserProfile(profile: Partial<InsertUserProfile>): Promise<UserProfile> {
    if (!isSupabaseConfigured) throw new Error("Service not configured");
    const userId = await getCurrentUserId();
    if (!userId) throw new Error("Not authenticated");

    const { data, error } = await supabase
      .from("user_profiles")
      .update({
        ...profile,
        updated_at: new Date().toISOString(),
      })
      .eq("user_id", userId)
      .select()
      .single();

    if (error) {
      console.error("Error updating user profile:", error);
      throw error;
    }

    this.setCachedProfile(data);
    return data;
  }

  async upsertUserProfile(profile: Partial<InsertUserProfile>): Promise<UserProfile> {
    if (!isSupabaseConfigured) throw new Error("Service not configured");
    const userId = await getCurrentUserId();
    if (!userId) throw new Error("Not authenticated");

    // Check if profile exists first
    const existing = await this.getUserProfile();
    
    if (existing) {
      // Update existing profile
      const { data, error } = await supabase
        .from("user_profiles")
        .update({
          ...profile,
          updated_at: new Date().toISOString(),
        })
        .eq("user_id", userId)
        .select()
        .single();

      if (error) {
        console.error("Error updating user profile:", error);
        throw error;
      }

      this.setCachedProfile(data);
      return data;
    } else {
      // Create new profile with generated ID
      const { data, error } = await supabase
        .from("user_profiles")
        .insert({
          id: crypto.randomUUID(),
          user_id: userId,
          ...profile,
          updated_at: new Date().toISOString(),
        })
        .select()
        .single();

      if (error) {
        console.error("Error creating user profile:", error);
        throw error;
      }

      this.setCachedProfile(data);
      return data;
    }
  }

  async deleteUserProfile(): Promise<void> {
    if (!isSupabaseConfigured) throw new Error("Service not configured");
    const userId = await getCurrentUserId();
    if (!userId) throw new Error("Not authenticated");

    const { error } = await supabase
      .from("user_profiles")
      .delete()
      .eq("user_id", userId);

    if (error) {
      console.error("Error deleting user profile:", error);
      throw error;
    }

    this.setCachedProfile(null);
  }

  async getProfile(): Promise<(UserProfile & { avatarUrl?: string }) | null> {
    const profile = await this.getUserProfile();
    if (!profile) return null;
    return {
      ...profile,
      avatarUrl: (profile as any).avatar_url,
    };
  }

  async updateProfile(updates: { avatarUrl?: string } & Partial<InsertUserProfile>): Promise<UserProfile> {
    const { avatarUrl, ...rest } = updates;
    const profileUpdates: any = { ...rest };
    if (avatarUrl !== undefined) {
      profileUpdates.avatar_url = avatarUrl;
    }
    return this.upsertUserProfile(profileUpdates);
  }

  // Helper function to check if file is text-based
  private isTextBasedDocument(mimeType: string, fileName?: string): boolean {
    // PDFs
    if (mimeType === 'application/pdf') {
      return true;
    }

    // Text files
    if (mimeType.startsWith('text/')) {
      return true;
    }

    // Microsoft Office documents
    if (mimeType === 'application/msword' ||
        mimeType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
        mimeType === 'application/vnd.ms-excel' ||
        mimeType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
        mimeType === 'application/vnd.ms-powerpoint' ||
        mimeType === 'application/vnd.openxmlformats-officedocument.presentationml.presentation') {
      return true;
    }

    // OpenDocument formats
    if (mimeType === 'application/vnd.oasis.opendocument.text' ||
        mimeType === 'application/vnd.oasis.opendocument.spreadsheet' ||
        mimeType === 'application/vnd.oasis.opendocument.presentation') {
      return true;
    }

    // JSON, XML, CSV
    if (mimeType === 'application/json' ||
        mimeType === 'application/xml' ||
        mimeType === 'text/xml' ||
        mimeType === 'text/csv') {
      return true;
    }

    // Check by file extension as fallback
    if (fileName) {
      const ext = fileName.toLowerCase().split('.').pop() || '';
      const textExtensions = [
        'pdf', 'doc', 'docx', 'txt', 'md', 'markdown',
        'xls', 'xlsx', 'csv', 'ppt', 'pptx',
        'odt', 'ods', 'odp', 'rtf',
        'json', 'xml', 'html', 'htm'
      ];
      if (textExtensions.includes(ext)) {
        return true;
      }
    }

    return false;
  }

  // Resume text extraction functionality (replaces upload)
  async extractResumeText(file: File): Promise<{ 
    fileName: string; 
    mimeType: string; 
    fileSize: number;
    extractedText: string;
  }> {
    if (!isSupabaseConfigured) throw new Error("Service not configured");
    const userId = await getCurrentUserId();
    if (!userId) throw new Error("Not authenticated");

    // Check if file is text-based
    if (!this.isTextBasedDocument(file.type, file.name)) {
      throw new Error("Invalid file type. Please upload a text-based document (PDF, DOC, DOCX, TXT, etc.).");
    }

    // Validate file size (max 10MB)
    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      throw new Error("File too large. Maximum size is 10MB.");
    }

    // Import extractTextFromBlob function
    const { extractTextFromBlob } = await import('@/utils/textExtractionHelper');

    // Extract text from file
    const blob = new Blob([file], { type: file.type });
    const extracted = await extractTextFromBlob(blob, file.name, file.type);

    if (extracted.error) {
      throw new Error(extracted.error);
    }

    if (!extracted.text || extracted.text.trim().length === 0) {
      throw new Error("No text could be extracted from this file. Please ensure the file contains readable text.");
    }

    // Update profile with extracted text and file metadata
    // Use snake_case for database columns
    await this.upsertUserProfile({
      resume_text: extracted.text,
      resume_file_name: file.name,
      resume_mime_type: file.type,
      resume_file_size: file.size,
    } as any);

    return {
      fileName: file.name,
      mimeType: file.type,
      fileSize: file.size,
      extractedText: extracted.text,
    };
  }

  async deleteResume(): Promise<void> {
    if (!isSupabaseConfigured) throw new Error("Service not configured");
    const userId = await getCurrentUserId();
    if (!userId) throw new Error("Not authenticated");

    // Clear resume fields from profile (use snake_case for DB columns)
    await this.upsertUserProfile({
      resume_text: null,
      resume_file_name: null,
      resume_mime_type: null,
      resume_file_size: null,
      resume_url: null, // Keep for backward compatibility
    } as any);
  }

  // Response preferences methods
  async getResponsePreferences(): Promise<ResponsePreferences> {
    const profile = await this.getUserProfile();
    // Check both snake_case (DB column) and camelCase
    const respPrefs = (profile as any)?.response_preferences || profile?.responsePreferences;
    if (!respPrefs) {
      return DEFAULT_RESPONSE_PREFERENCES;
    }
    return {
      ...DEFAULT_RESPONSE_PREFERENCES,
      ...(respPrefs as Partial<ResponsePreferences>)
    };
  }

  async updateResponsePreferences(preferences: Partial<ResponsePreferences>): Promise<ResponsePreferences> {
    const current = await this.getResponsePreferences();
    const updated = { ...current, ...preferences };
    
    // Use snake_case for database column
    await this.upsertUserProfile({
      response_preferences: updated,
    } as any);
    
    return updated;
  }

  async getDocumentNamingPreferences(): Promise<DocumentNamingPreferences> {
    const profile = await this.getUserProfile();
    const prefs = (profile?.preferences as Record<string, unknown> | null) || null;
    const namingPrefs = prefs?.documentNaming;
    if (!namingPrefs || typeof namingPrefs !== "object") {
      return DEFAULT_DOCUMENT_NAMING_PREFERENCES;
    }
    return resolveDocumentNamingPreferences(namingPrefs as Partial<DocumentNamingPreferences>);
  }

  async updateDocumentNamingPreferences(
    preferences: Partial<DocumentNamingPreferences>
  ): Promise<DocumentNamingPreferences> {
    const current = await this.getDocumentNamingPreferences();
    const updated = resolveDocumentNamingPreferences({ ...current, ...preferences });
    const profile = await this.getUserProfile();
    const existingPrefs = (profile?.preferences as Record<string, unknown> | null) || {};

    await this.upsertUserProfile({
      preferences: {
        ...existingPrefs,
        documentNaming: updated,
      },
    } as any);

    return updated;
  }

  async getLibraryFavorites(): Promise<LibraryDocRef[]> {
    const profile = await this.getUserProfile();
    const prefs = (profile?.preferences as Record<string, unknown> | null) || null;
    return normalizeLibraryDocRefs(prefs?.libraryFavorites);
  }

  async updateLibraryFavorites(favorites: LibraryDocRef[]): Promise<LibraryDocRef[]> {
    const profile = await this.getUserProfile();
    const existingPrefs = (profile?.preferences as Record<string, unknown> | null) || {};
    const normalized = normalizeLibraryDocRefs(favorites);

    await this.upsertUserProfile({
      preferences: {
        ...existingPrefs,
        libraryFavorites: normalized,
      },
    } as any);

    return normalized;
  }

  async toggleLibraryFavorite(ref: LibraryDocRef): Promise<LibraryDocRef[]> {
    const current = await this.getLibraryFavorites();
    const exists = current.some((f) => f.id === ref.id && f.itemType === ref.itemType);
    const updated = exists
      ? current.filter((f) => !(f.id === ref.id && f.itemType === ref.itemType))
      : [...current, ref];
    return this.updateLibraryFavorites(updated);
  }

  async getSourceOfTruthDocIds(): Promise<string[]> {
    const profile = await this.getUserProfile();
    const prefs = (profile?.preferences as Record<string, unknown> | null) || null;
    return normalizeSourceOfTruthDocIds(prefs?.sourceOfTruthDocIds);
  }

  async isSourceOfTruthEnabled(): Promise<boolean> {
    const profile = await this.getUserProfile();
    const prefs = (profile?.preferences as Record<string, unknown> | null) || null;
    const ids = normalizeSourceOfTruthDocIds(prefs?.sourceOfTruthDocIds);
    return isSourceOfTruthEnabledFromPrefs(prefs, ids);
  }

  async updateSourceOfTruthDocIds(docIds: string[]): Promise<string[]> {
    const profile = await this.getUserProfile();
    const existingPrefs = (profile?.preferences as Record<string, unknown> | null) || {};
    const normalized = normalizeSourceOfTruthDocIds(docIds);

    await this.upsertUserProfile({
      preferences: {
        ...existingPrefs,
        sourceOfTruthDocIds: normalized,
        ...(normalized.length > 0 && existingPrefs.sourceOfTruthEnabled === undefined
          ? { sourceOfTruthEnabled: true }
          : {}),
      },
    } as InsertUserProfile);

    return normalized;
  }

  async setSourceOfTruthEnabled(enabled: boolean): Promise<void> {
    const profile = await this.getUserProfile();
    const existingPrefs = (profile?.preferences as Record<string, unknown> | null) || {};

    await this.upsertUserProfile({
      preferences: {
        ...existingPrefs,
        sourceOfTruthEnabled: enabled,
      },
    } as InsertUserProfile);
  }

  async addSourceOfTruthDocId(docId: string): Promise<string[]> {
    const current = await this.getSourceOfTruthDocIds();
    if (current.includes(docId)) return current;
    if (current.length >= MAX_SOURCE_OF_TRUTH_DOCS) {
      throw new Error(`Maximum ${MAX_SOURCE_OF_TRUTH_DOCS} Source of Truth documents allowed`);
    }
    return this.updateSourceOfTruthDocIds([...current, docId]);
  }

  async removeSourceOfTruthDocId(docId: string): Promise<string[]> {
    const current = await this.getSourceOfTruthDocIds();
    return this.updateSourceOfTruthDocIds(current.filter((id) => id !== docId));
  }

  async toggleSourceOfTruthDocId(docId: string): Promise<string[]> {
    const current = await this.getSourceOfTruthDocIds();
    if (current.includes(docId)) {
      return this.removeSourceOfTruthDocId(docId);
    }
    return this.addSourceOfTruthDocId(docId);
  }

  // Generate system prompt instructions from response preferences
  generateResponseInstructions(prefs: ResponsePreferences): string {
    const instructions: string[] = [];

    if (prefs.includeUrls) {
      instructions.push("When mentioning any website, tool, resource, or service, ALWAYS include a clickable URL link to it.");
    }
    if (prefs.includeCosts) {
      instructions.push("When relevant, include estimated costs, pricing, or budget considerations.");
    }
    if (prefs.includeActionPlans) {
      instructions.push("Provide clear, numbered step-by-step action plans when explaining how to do something.");
    }
    if (prefs.useBulletPoints) {
      instructions.push("Use bullet points for lists and key information. Avoid long paragraphs unless absolutely necessary.");
    }
    if (prefs.includeTimelines) {
      instructions.push("Include realistic time estimates for tasks and projects.");
    }
    if (prefs.includeAlternatives) {
      instructions.push("Suggest alternative approaches or options when applicable.");
    }
    if (prefs.citeSources) {
      instructions.push("Cite sources and references when providing factual information.");
    }
    if (prefs.showConfidence) {
      instructions.push("Indicate your confidence level (high/medium/low) for claims or recommendations.");
    }
    if (prefs.maxParagraphLength && prefs.maxParagraphLength < 5) {
      instructions.push(`Keep paragraphs concise - no more than ${prefs.maxParagraphLength} sentences each.`);
    }
    if (prefs.includeExamples) {
      instructions.push("Include practical examples to illustrate concepts when helpful.");
    }
    if (prefs.includeWarnings) {
      instructions.push("Highlight potential risks, warnings, or things to watch out for.");
    }
    if (prefs.customInstructions?.trim()) {
      instructions.push(prefs.customInstructions.trim());
    }

    return instructions.length > 0 
      ? `\n\nUser's Response Style Preferences:\n${instructions.map(i => `- ${i}`).join('\n')}`
      : '';
  }
}

export const userProfileService = new UserProfileService();

export function generateResponseInstructions(prefs: ResponsePreferences): string {
  return userProfileService.generateResponseInstructions(prefs);
}
